import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { AccountStatus, ActivityKind, AdminUser, AppRole } from '@exigo/shared';
import { PageHeader } from '../../components/layout/PageHeader';
import { Avatar } from '../../components/ui/Avatar';
import { Badge, type BadgeTone } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Select } from '../../components/ui/Select';
import { Spinner } from '../../components/ui/Spinner';
import { Tabs } from '../../components/ui/Tabs';
import { TextInput } from '../../components/ui/TextInput';
import {
  useActivityFeedQuery,
  useAdminUsersQuery,
  useAllScenariosQuery,
  useUpdateUserMutation,
} from '../../api/queries';
import styles from './AdminPage.module.css';

/**
 * Administration: the access queue, everyone's activity, and every scenario.
 *
 * Rendered behind `RequireRole requiredRole="Admin"`, which is a convenience —
 * the real boundary is `requireAdmin` on the API's `/admin` prefix. Everything
 * here would 403 for a non-admin regardless of what this component renders.
 */

type Section = 'access' | 'activity' | 'scenarios';

const STATUS_TONE: Record<AccountStatus, BadgeTone> = {
  active: 'ok',
  pending: 'warn',
  rejected: 'danger',
  disabled: 'neutral',
};

const STATUS_LABEL: Record<AccountStatus, string> = {
  active: 'Active',
  pending: 'Pending',
  rejected: 'Rejected',
  disabled: 'Disabled',
};

/**
 * One readable sentence per event kind. A lookup rather than string
 * concatenation at the call site, so every event reads consistently and a new
 * kind cannot ship without someone writing its wording.
 */
const ACTIVITY_VERB: Record<ActivityKind, string> = {
  signed_in: 'signed in',
  scenario_created: 'created',
  scenario_updated: 'edited',
  scenario_finalized: 'finalized',
  scenario_deleted: 'deleted',
  access_approved: 'approved access for',
  access_rejected: 'rejected access for',
  access_revoked: 'revoked access for',
  role_changed: 'changed the role of',
};

const ACTIVITY_FILTERS: { value: string; label: string }[] = [
  { value: 'all', label: 'All activity' },
  { value: 'signed_in', label: 'Sign-ins' },
  { value: 'scenario_created', label: 'Scenarios created' },
  { value: 'scenario_finalized', label: 'Scenarios finalized' },
  { value: 'scenario_deleted', label: 'Scenarios deleted' },
  { value: 'access_approved', label: 'Access approved' },
  { value: 'role_changed', label: 'Role changes' },
];

export function AdminPage(): React.JSX.Element {
  const [section, setSection] = useState<Section>('access');
  const users = useAdminUsersQuery();

  const pendingCount = users.data?.filter((u) => u.status === 'pending').length ?? 0;

  return (
    <>
      <PageHeader
        title="Administration"
        meta={
          pendingCount > 0
            ? `${String(pendingCount)} account${pendingCount === 1 ? '' : 's'} waiting for approval`
            : 'Access, activity and scenarios'
        }
        tabs={
          <Tabs
            value={section}
            onChange={(v) => { setSection(v as Section); }}
            ariaLabel="Administration section"
            items={[
              {
                value: 'access',
                label: 'Access',
                ...(users.data ? { count: users.data.length } : {}),
              },
              { value: 'activity', label: 'Activity' },
              { value: 'scenarios', label: 'All scenarios' },
            ]}
          />
        }
      />

      <div className={styles.body}>
        {section === 'access' && <AccessSection />}
        {section === 'activity' && <ActivitySection />}
        {section === 'scenarios' && <ScenariosSection />}
      </div>
    </>
  );
}

// --- Access ----------------------------------------------------------------

function AccessSection(): React.JSX.Element {
  const users = useAdminUsersQuery();
  const update = useUpdateUserMutation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users.data ?? [];
    return (users.data ?? []).filter(
      (u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term),
    );
  }, [users.data, search]);

  if (users.isLoading) return <Loading />;
  if (users.isError) {
    return <EmptyState title="Could not load accounts" body="Check the API is running and try again." />;
  }

  const pending = filtered.filter((u) => u.status === 'pending');
  const rest = filtered.filter((u) => u.status !== 'pending');

  return (
    <div className={styles.stack}>
      {/* The approval queue is the reason this page exists, so it gets its own
          block above the full table rather than being a filter of it. */}
      {pending.length > 0 && (
        <section className={styles.panel}>
          <header className={styles.panelHead}>
            <h2 className={styles.panelTitle}>Waiting for approval</h2>
            <p className={styles.panelSub}>
              These people have signed in successfully but cannot use the application yet.
            </p>
          </header>
          <ul className={styles.queue}>
            {pending.map((user) => (
              <li key={user.id} className={styles.queueRow}>
                <Avatar name={user.name} size="md" />
                <div className={styles.queueWho}>
                  <p className={styles.queueName}>{user.name}</p>
                  <p className={styles.queueEmail}>{user.email}</p>
                </div>
                <p className={styles.queueMeta}>Requested {formatWhen(user.createdAt)}</p>
                <div className={styles.queueActions}>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={update.isPending}
                    onClick={() => { update.mutate({ id: user.id, patch: { status: 'active' } }); }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={update.isPending}
                    onClick={() => { update.mutate({ id: user.id, patch: { status: 'rejected' } }); }}
                  >
                    Reject
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className={styles.panel}>
        <header className={styles.panelHead}>
          <div>
            <h2 className={styles.panelTitle}>All accounts</h2>
            <p className={styles.panelSub}>
              Role and access can be changed at any time and take effect on the person&rsquo;s next
              request — they do not need to sign in again.
            </p>
          </div>
          <div className={styles.search}>
            <TextInput
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              aria-label="Search accounts"
            />
          </div>
        </header>

        {update.isError && (
          <p className={styles.error} role="alert">
            {update.error.message}
          </p>
        )}

        {rest.length === 0 && pending.length === 0 ? (
          <EmptyState title="No accounts match" body="Try a different search term." />
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Person</th>
                  <th scope="col">Role</th>
                  <th scope="col">Access</th>
                  <th scope="col" className={styles.num}>Scenarios</th>
                  <th scope="col">Last activity</th>
                  <th scope="col">Approved by</th>
                </tr>
              </thead>
              <tbody>
                {rest.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    busy={update.isPending}
                    onChange={(patch) => { update.mutate({ id: user.id, patch }); }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function UserRow({
  user,
  busy,
  onChange,
}: {
  user: AdminUser;
  busy: boolean;
  onChange: (patch: { role?: AppRole; status?: AccountStatus }) => void;
}): React.JSX.Element {
  return (
    <tr>
      <td>
        <div className={styles.person}>
          <Avatar name={user.name} size="sm" />
          <div>
            <p className={styles.personName}>{user.name}</p>
            <p className={styles.personEmail}>{user.email}</p>
          </div>
        </div>
      </td>
      <td>
        <Select
          value={user.role}
          onChange={(v) => { onChange({ role: v as AppRole }); }}
          options={[
            { value: 'User', label: 'User' },
            { value: 'Admin', label: 'Admin' },
          ]}
          ariaLabel={`Role for ${user.name}`}
          disabled={busy}
        />
      </td>
      <td>
        <div className={styles.access}>
          <Badge tone={STATUS_TONE[user.status]} dot>
            {STATUS_LABEL[user.status]}
          </Badge>
          {user.status === 'active' ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => { onChange({ status: 'disabled' }); }}
            >
              Revoke
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => { onChange({ status: 'active' }); }}
            >
              Restore
            </Button>
          )}
        </div>
      </td>
      <td className={styles.num}>{user.scenarioCount}</td>
      <td className={styles.when}>{user.lastActivityAt ? formatWhen(user.lastActivityAt) : '—'}</td>
      <td className={styles.when}>{user.approvedByName ?? '—'}</td>
    </tr>
  );
}

// --- Activity --------------------------------------------------------------

function ActivitySection(): React.JSX.Element {
  const [kind, setKind] = useState('all');
  const [who, setWho] = useState('all');
  const users = useAdminUsersQuery();

  const feed = useActivityFeedQuery({
    ...(kind !== 'all' ? { kind: kind as ActivityKind } : {}),
    ...(who !== 'all' ? { userId: Number(who) } : {}),
  });

  const whoOptions = [
    { value: 'all', label: 'Everyone' },
    ...(users.data ?? []).map((u) => ({ value: String(u.id), label: u.name })),
  ];

  // Pages append, so this is every event loaded so far, newest first.
  const events = feed.data?.pages.flatMap((page) => page.events) ?? [];

  return (
    <section className={styles.panel}>
      <header className={styles.panelHead}>
        <div>
          <h2 className={styles.panelTitle}>Activity</h2>
          <p className={styles.panelSub}>
            Sign-ins, access decisions and deliberate scenario changes. Continuous editing in the
            calculator is not listed individually — the &ldquo;Last activity&rdquo; column on the
            Access tab accounts for it.
          </p>
        </div>
        <div className={styles.filters}>
          <Select value={who} onChange={setWho} options={whoOptions} ariaLabel="Filter by person" />
          <Select
            value={kind}
            onChange={setKind}
            options={ACTIVITY_FILTERS}
            ariaLabel="Filter by activity type"
          />
        </div>
      </header>

      {feed.isError ? (
        <EmptyState title="Could not load activity" body="Check the API is running and try again." />
      ) : !feed.data ? (
        <Loading />
      ) : events.length === 0 ? (
        <EmptyState title="Nothing here yet" body="No activity matches these filters." />
      ) : (
        <ol className={styles.feed}>
          {events.map((event) => (
            <li key={event.id} className={styles.event}>
              <Avatar name={event.actorName ?? 'Unknown'} size="sm" />
              <p className={styles.eventText}>
                <strong>{event.actorName ?? 'An account'}</strong> {ACTIVITY_VERB[event.kind]}
                {event.scenarioName !== null && <> &ldquo;{event.scenarioName}&rdquo;</>}
                {event.targetName !== null && <> {event.targetName}</>}
                <EventDetail detail={event.detail} />
              </p>
              <time className={styles.eventWhen} dateTime={event.createdAt}>
                {formatWhen(event.createdAt)}
              </time>
            </li>
          ))}
        </ol>
      )}

      {events.length > 0 && (
        <div className={styles.more}>
          <p className={styles.moreCount}>
            {String(events.length)} event{events.length === 1 ? '' : 's'}
            {feed.hasNextPage ? ' so far' : ' — that’s all of them'}
          </p>
          {feed.hasNextPage && (
            <Button
              variant="secondary"
              size="sm"
              disabled={feed.isFetchingNextPage}
              onClick={() => { void feed.fetchNextPage(); }}
            >
              {feed.isFetchingNextPage ? 'Loading…' : 'Load older activity'}
            </Button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The `from`/`to` pair a role or status change carries. Only that shape is
 * rendered — any other `detail` keys a future event kind adds are for the log,
 * not for this sentence, and are skipped rather than dumped into it.
 */
function EventDetail({ detail }: { detail: Record<string, string> | null }): React.JSX.Element | null {
  if (detail === null) return null;
  const { from, to } = detail;
  if (!from || !to) return null;
  return (
    <span className={styles.eventDetail}>
      {' '}
      ({from} → {to})
    </span>
  );
}

// --- All scenarios ---------------------------------------------------------

function ScenariosSection(): React.JSX.Element {
  const all = useAllScenariosQuery();

  if (all.isError) {
    return <EmptyState title="Could not load scenarios" body="Check the API is running and try again." />;
  }
  if (!all.data) return <Loading />;
  if (all.data.length === 0) {
    return <EmptyState title="No scenarios yet" body="Nobody has created a scenario." />;
  }

  return (
    <section className={styles.panel}>
      <header className={styles.panelHead}>
        <div>
          <h2 className={styles.panelTitle}>Every scenario</h2>
          <p className={styles.panelSub}>
            Read-only across all users. Opening one shows the full calculation; editing and
            deleting stay with the owner.
          </p>
        </div>
      </header>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Scenario</th>
              <th scope="col">Owner</th>
              <th scope="col">Customer</th>
              <th scope="col">Status</th>
              <th scope="col">Last updated</th>
            </tr>
          </thead>
          <tbody>
            {all.data.map((scenario) => (
              <tr key={scenario.id}>
                <td>
                  <Link className={styles.link} to={`/scenarios/${scenario.id}`}>
                    {scenario.name}
                  </Link>
                </td>
                <td>{scenario.ownerName ?? '—'}</td>
                <td>{scenario.customerName ?? '—'}</td>
                <td>
                  <Badge tone={scenario.status === 'finalized' ? 'ok' : 'neutral'} dot>
                    {scenario.status === 'finalized' ? 'Finalized' : 'Draft'}
                  </Badge>
                </td>
                <td className={styles.when}>{formatWhen(scenario.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// --- Shared ----------------------------------------------------------------

function Loading(): React.JSX.Element {
  return (
    <div className={styles.loading}>
      <Spinner size={22} />
    </div>
  );
}

/**
 * Relative for anything recent, absolute past a week. "3 hours ago" is what an
 * admin is actually asking when they scan a "last activity" column; a full
 * timestamp is what they want once it stops being recent.
 */
function formatWhen(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} min ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days <= 7) return `${String(days)} day${days === 1 ? '' : 's'} ago`;

  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
